import { useState, useEffect } from 'react';
import { supabase } from '../utils/supabase';
import { createLogger } from '../utils/logger';

const logger = createLogger({ module: 'useMissionControlActions' });

export interface MissionControlAction {
  id: string;
  industry_type: string;
  phase: string;
  action_order: number;
  title: string;
  description: string;
  icon_emoji: string | null;
  context_title: string | null;
  context_placeholder: string | null;
  default_impact: number;
  default_engagement: number;
}

export interface MissionControlContext {
  title?: string;
  placeholder?: string;
  defaultImpact?: number;
  defaultEngagement?: number;
}

// Fallback actions in case database fetch fails
const FALLBACK_ACTIONS: Record<string, MissionControlAction[]> = {
  foundation: [
    {
      id: 'fallback-1',
      industry_type: 'generic',
      phase: 'foundation',
      action_order: 1,
      title: 'Document your first contribution',
      description: 'Show you can work independently',
      icon_emoji: '📝',
      context_title: 'First Contribution',
      context_placeholder: 'Describe your first meaningful contribution. What did you work on? What did you learn? How did you collaborate with the team?',
      default_impact: 3,
      default_engagement: 4
    },
    {
      id: 'fallback-2',
      industry_type: 'generic',
      phase: 'foundation',
      action_order: 2,
      title: 'Schedule 1:1 with team members',
      description: 'Build relationships and understand team dynamics',
      icon_emoji: '🤝',
      context_title: 'Team 1:1 Meeting',
      context_placeholder: 'Who did you meet with? What did you learn about their role and the team? What insights did you gain?',
      default_impact: 2,
      default_engagement: 4
    },
    {
      id: 'fallback-3',
      industry_type: 'generic',
      phase: 'foundation',
      action_order: 3,
      title: 'Review your goals with manager',
      description: 'Ensure alignment on expectations',
      icon_emoji: '🎯',
      context_title: 'Goals Review with Manager',
      context_placeholder: 'What goals did you discuss? How do they align with team priorities? What feedback did you receive?',
      default_impact: 3,
      default_engagement: 3
    }
  ]
};

export function useMissionControlActions(industryType: string | null | undefined, phase: string) {
  const [actions, setActions] = useState<MissionControlAction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!industryType || !phase) {
      logger.debug('Missing industryType or phase', { industryType, phase });
      setActions(FALLBACK_ACTIONS[phase] || []);
      setLoading(false);
      return;
    }

    const fetchActions = async () => {
      try {
        setLoading(true);
        logger.debug('Fetching mission control actions', { industryType, phase });

        // Fetch both industry-specific and generic actions in a single query
        const typesToFetch = industryType === 'generic' 
          ? ['generic'] 
          : [industryType, 'generic'];

        const { data, error: fetchError } = await supabase
          .from('mission_control_actions')
          .select('*')
          .in('industry_type', typesToFetch)
          .eq('phase', phase)
          .order('industry_type', { ascending: false }) // Industry-specific first, then generic
          .order('action_order', { ascending: true });

        if (fetchError) {
          throw fetchError;
        }

        if (!data || data.length === 0) {
          logger.warn('No actions found in database, using fallback', { industryType, phase });
          setActions(FALLBACK_ACTIONS[phase] || []);
        } else {
          // Filter to use industry-specific actions if available, otherwise use generic
          const industrySpecificActions = data.filter(action => action.industry_type === industryType);
          const genericActions = data.filter(action => action.industry_type === 'generic');
          
          const actionsToUse = industrySpecificActions.length > 0 
            ? industrySpecificActions 
            : genericActions;

          logger.debug('Mission control actions fetched successfully', { 
            totalFetched: data.length,
            industrySpecificCount: industrySpecificActions.length,
            genericCount: genericActions.length,
            usingType: industrySpecificActions.length > 0 ? industryType : 'generic',
            phase 
          });
          
          setActions(actionsToUse);
        }
      } catch (err) {
        logger.error('Error fetching mission control actions:', err);
        setError(err as Error);
        // Use fallback actions on error
        setActions(FALLBACK_ACTIONS[phase] || []);
      } finally {
        setLoading(false);
      }
    };

    fetchActions();
  }, [industryType, phase]);

  return { actions, loading, error };
}

// Helper function to convert action to context format
export function actionToContext(action: MissionControlAction): MissionControlContext | undefined {
  if (!action.context_title) return undefined;
  
  return {
    title: action.context_title,
    placeholder: action.context_placeholder || undefined,
    defaultImpact: action.default_impact,
    defaultEngagement: action.default_engagement
  };
}